import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <whatsapp-order-button>
 *
 * Wraps the WhatsApp order link inside the buy-buttons block.
 * Listens to the same `ProductSelectEvent` that Horizon's variant-picker
 * dispatches, so the WhatsApp message text (title / variant / price)
 * always matches whatever variant the shopper currently has selected —
 * without a page reload.
 *
 * Expected data-* attributes (set from blocks/buy-buttons.liquid):
 *  - data-phone            WhatsApp number, digits only (no + or spaces)
 *  - data-message-template Raw template with {product_title} {variant_title} {price} {product_url} placeholders
 *  - data-product-title    Current product title
 *  - data-product-url      Canonical product URL (kept static across variant changes)
 *  - data-money-format     shop.money_format, used to (re)format the price client-side
 *  - data-currency         Active currency ISO code
 */
class WhatsAppOrderButton extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    if (this.section) {
      this.section.addEventListener(StandardEvents.productSelect, this.handleProductSelect);
    }
  }

  disconnectedCallback() {
    if (this.section) {
      this.section.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
    }
  }

  /** @param {CustomEvent & {target: EventTarget, promise: Promise<any>}} event */
  handleProductSelect = (event) => {
    // Ignore selection events bubbling up from unrelated product cards (e.g. recommendations)
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const variant = detail?.resource;
        if (!variant) return;
        this.updateLink(variant);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[whatsapp-order-button] event promise rejected:', error);
        }
      });
  };

  /** @param {Record<string, any>} variant */
  updateLink(variant) {
    const link = this.querySelector('[ref="whatsappLink"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const template = this.dataset.messageTemplate || '';
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    const phone = (this.dataset.phone || '').replace(/[^0-9]/g, '');

    const priceFormatted = formatMoney(variant.price, moneyFormat, currency);
    const variantTitle = variant.public_title || variant.title || '';

    const message = template
      .replaceAll('{product_title}', this.dataset.productTitle || '')
      .replaceAll('{variant_title}', variantTitle)
      .replaceAll('{price}', priceFormatted)
      .replaceAll('{product_url}', this.dataset.productUrl || '');

    link.href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    const unavailable = variant.available === false;
    link.classList.toggle('whatsapp-order-button--disabled', unavailable);
    link.toggleAttribute('aria-disabled', unavailable);
  }
}

if (!customElements.get('whatsapp-order-button')) {
  customElements.define('whatsapp-order-button', WhatsAppOrderButton);
}
